import type { InferOutput } from "valibot";
import { object } from "valibot";
import type { IncompleteIntegrationDefinition } from "../types";
import { initIntegrationDefinition } from "../Integration";
import { requireText } from "../../utils/validation/base";
import { MAX_CHARACTERS_SM } from "../../constants";
import { TextInput } from "../../components/inputs/TextInput";
import { useTranslation } from "../../state/i18n";
import MixedInput from "../../components/inputs/MixedInput";
import { parseStringTemplate, stringTemplateToString } from "../../utils/stringTemplateUtil";
import { createMemo } from "solid-js";
import { metaVariableOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import type { FieldType } from "../../types";
import { useFormServices } from "../../services/formServices";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import type { VariableType } from "../../variables/types";
import { toVariableValueOption } from "../../variables/variableUtil";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";

const ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

const ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

type ConfigurationDto = {
  server: string;
  path: string;
  username?: string;
  password?: string;
};
const Schema = object({
  server: requireText(MAX_CHARACTERS_SM),
  path: stringTemplate(
    ALLOWED_PLACEHOLDER_FIELDS,
    ALLOWED_PLACEHOLDER_VARIABLES,
    [
      "info.userId",
      "meta.location.longitude",
      "meta.location.latitude",
      "meta.registrationDate",
      "meta.guid",
      "meta.taskId",
      "meta.serialNumber",
      "info.formName",
      "info.summary",
    ],
    MAX_CHARACTERS_SM,
  ),
  username: requireText(MAX_CHARACTERS_SM),
  password: requireText(MAX_CHARACTERS_SM),
});

export type WebDAVIntegrationConfiguration = InferOutput<typeof Schema>;
const WebDAVIntegration: IncompleteIntegrationDefinition<WebDAVIntegrationConfiguration, ConfigurationDto> = {
  type: "webdav",
  legacy: {
    namespace: "com.moreapp",
    key: "webdav",
    version: 3,
  },
  schema: Schema,
  icon: integrationsIcons["webdav"],
  titleKey: keyFromSelector(($) => $.WEBDAV_INTEGRATION_TITLE),
  descriptionKey: keyFromSelector(($) => $.WEBDAV_INTEGRATION_DESCRIPTION),
  verifiable: true,
  defaultProperties: () => ({
    server: "",
    path: [],
    username: "",
    password: "",
  }),
  propertyKeys: {
    server: keyFromSelector(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_SERVER_LABEL),
    path: keyFromSelector(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_PATH_LABEL),
    username: keyFromSelector(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_USERNAME_LABEL),
    password: keyFromSelector(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_PASSWORD_LABEL),
  },
  fromDto: (configuration, fields, variables) => ({
    server: configuration.server,
    path: parseStringTemplate(configuration.path, fields, variables),
    username: configuration.username,
    password: configuration.password,
  }),
  toDto: (configuration, fields, variables) => ({
    server: configuration.server,
    path: stringTemplateToString(configuration.path, fields, variables),
    username: configuration.username,
    password: configuration.password,
  }),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    const placeholderMenuOptions = createMemo(() =>
      createDynamicValueOptions(t, {
        fields: fieldService
          .getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS)
          .flatMap((field) => toFieldValueOptions(field)),
        subforms: [],
        customVariables: variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES).map(toVariableValueOption),
        metaVariables: [
          metaVariableOption("info.userId"),
          metaVariableOption("meta.location.longitude"),
          metaVariableOption("meta.location.latitude"),
          metaVariableOption("meta.registrationDate"),
          metaVariableOption("meta.guid"),
          metaVariableOption("meta.taskId"),
          metaVariableOption("meta.serialNumber"),
          metaVariableOption("info.formName"),
          metaVariableOption("info.summary"),
        ],
      }),
    );

    return (
      <>
        <TextInput
          label={t(props.propertyKeys.server)}
          value={props.integration.configuration.server}
          onBlur={(e) => {
            props.updateConfiguration("server", e.target.value);
          }}
          errorMessage={props.getError("server")}
          required
          info={{
            title: t(props.propertyKeys.server),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.server }),
            description: t(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_SERVER_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.path)}
          value={props.integration.configuration.path}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={placeholderMenuOptions()}
          onChange={(value) => {
            props.updateConfiguration("path", value);
          }}
          errorMessage={props.getError("path")}
          required
          info={{
            title: t(props.propertyKeys.path),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.path }),
            description: t(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_PATH_DESCRIPTION),
          }}
        />

        <TextInput
          label={t(props.propertyKeys.username)}
          value={props.integration.configuration.username}
          onBlur={(e) => {
            props.updateConfiguration("username", e.target.value);
          }}
          errorMessage={props.getError("username")}
          required
          info={{
            title: t(props.propertyKeys.username),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.username }),
            description: t(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_USERNAME_DESCRIPTION),
          }}
        />

        <TextInput
          label={t(props.propertyKeys.password)}
          value={props.integration.configuration.password}
          onBlur={(e) => {
            props.updateConfiguration("password", e.target.value);
          }}
          errorMessage={props.getError("password")}
          required
          info={{
            title: t(props.propertyKeys.password),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.password }),
            description: t(($) => $.WEBDAV_INTEGRATION_CONFIGURATION_PASSWORD_DESCRIPTION),
          }}
        />
      </>
    );
  },
};

export default initIntegrationDefinition(WebDAVIntegration);
